import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence } from "framer-motion";
import { Activity, ChevronLeft, Radio } from "lucide-react";
import { useCaseQuery, usePoll } from "hooks/useCaseDashboard";
import { caseMode, mergeById } from "utils/caseDashboard";
import { liveStats } from "utils/caseLiveStats";
import { Card, EmptyState, Notice, QueryState, SecondaryButton } from "./parts";
import { useNow } from "./live/shared";
import { AlertList, KpiRow, LiveHeader } from "./live/LiveTop";
import { ImpactFeed, ImpactTimeline } from "./live/ImpactCharts";
import { ImpactMix, PlayerGrid, PlayerPanel } from "./live/Players";

/** Live polling of the current session: roster every 2 s, new impacts every 2 s. */
function useLiveSession(caseId, enabled) {
  const current = useCaseQuery(caseId, "currentSession", {}, 5000, enabled);
  const sessionId = current.data?.id || null;
  const roster = useCaseQuery(
    caseId,
    "roster",
    { sessionId },
    2000,
    enabled && !!sessionId
  );

  const [impacts, setImpacts] = useState([]);
  const lastId = useRef(0);
  useEffect(() => {
    setImpacts([]);
    lastId.current = 0;
  }, [sessionId]);

  const poll = usePoll(
    async () => {
      const res = await window.electronAPI.caseDashboard.query(
        caseId,
        "impacts",
        {
          sessionId,
          afterId: lastId.current,
        }
      );
      if (res?.ok && res.data?.length) {
        lastId.current = res.data[res.data.length - 1].id;
        setImpacts((prev) => mergeById(prev, res.data));
      }
      return res;
    },
    2000,
    [caseId, sessionId],
    enabled && !!sessionId
  );

  return {
    session: current.data,
    loading: current.loading,
    error: current.error || roster.error || poll.error,
    roster: roster.data || [],
    impacts,
    updatedAt: Math.min(
      roster.updatedAt ?? Infinity,
      poll.updatedAt ?? Infinity
    ),
  };
}

const LiveSessionView = ({ caseId, mode, replaySessionId, onBack }) => {
  const { t } = useTranslation();
  const replay = !!replaySessionId;
  const live = useLiveSession(caseId, !replay);
  const detail = useCaseQuery(
    caseId,
    "sessionDetail",
    { sessionId: replaySessionId },
    0,
    replay
  );
  const now = useNow(1000, !replay);
  const [selected, setSelected] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const session = replay ? detail.data?.session : live.session;
  const roster = replay ? detail.data?.roster || [] : live.roster;
  const impacts = replay ? detail.data?.impacts || [] : live.impacts;

  const stats = useMemo(
    () =>
      session ? liveStats({ session, roster, impacts, now, replay }) : null,
    [session, roster, impacts, now, replay]
  );

  const select = useCallback((key) => {
    setSelected((cur) => (cur === key ? cur : key));
    setPanelOpen(true);
  }, []);
  const clear = useCallback(() => {
    setSelected(null);
    setPanelOpen(false);
  }, []);

  const backButton = replay && (
    <SecondaryButton onClick={onBack} className="mb-1">
      <ChevronLeft size={14} />
      {t("caseDashboard.live.backToHistory")}
    </SecondaryButton>
  );

  if (replay && detail.loading) {
    return (
      <p className="text-[13px] text-[#6B7280]">
        {t("caseDashboard.common.loading")}
      </p>
    );
  }

  if (!session || !stats) {
    return (
      <div className="space-y-4">
        {backButton}
        {replay ? (
          <QueryState query={detail} />
        ) : (
          <>
            {live.error && (
              <Notice tone="error">
                {t("caseDashboard.common.queryError", { message: live.error })}
              </Notice>
            )}
            <Card>
              <EmptyState
                icon={live.loading ? Radio : Activity}
                title={
                  live.loading
                    ? t("caseDashboard.common.loading")
                    : t("caseDashboard.live.noSession")
                }
                body={
                  live.loading ? null : t("caseDashboard.live.noSessionHint")
                }
              />
            </Card>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {backButton}
      <LiveHeader
        stats={stats}
        sessionId={session.id}
        replay={replay}
        now={now}
        updatedAt={Number.isFinite(live.updatedAt) ? live.updatedAt : null}
      />

      {!replay && caseMode(mode) === "pairing-only" && (
        <Notice>{t("caseDashboard.live.pairingOnlyNote")}</Notice>
      )}
      {!replay && live.error && (
        <Notice tone="error">
          {t("caseDashboard.common.queryError", { message: live.error })}
        </Notice>
      )}

      <AlertList alerts={stats.alerts} now={now} onSelect={select} />
      <KpiRow stats={stats} replay={replay} now={now} />

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <ImpactTimeline
            stats={stats}
            replay={replay}
            selected={selected}
            onSelect={select}
          />
        </div>
        <ImpactFeed
          stats={stats}
          replay={replay}
          now={now}
          selected={selected}
          onSelect={select}
          onClear={clear}
        />
      </div>

      <PlayerGrid
        stats={stats}
        replay={replay}
        now={now}
        onSelect={select}
        selected={selected}
      />
      <ImpactMix stats={stats} onSelect={select} selected={selected} />

      <AnimatePresence>
        {panelOpen && selected && (
          <PlayerPanel
            key="panel"
            stats={stats}
            playerKey={selected}
            replay={replay}
            now={now}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveSessionView;
